"use client"

import { TableCell } from "@/components/ui/table"
import { Button } from "@/components/ui/button"
import { Textarea } from "@/components/ui/textarea"
import { StickyNote, Edit, Save, X } from "lucide-react"
import type { UseFormReturn } from "react-hook-form"
import { ProcessedApplicant } from "@/utils/applicantsTable/customTypes"

interface NoteCellProps {
  applicant: ProcessedApplicant
  isEditing: boolean
  form: UseFormReturn<{ note: string }>
  onEditNote: (userId: string, currentNote: string) => void
  onSaveNote: (userId: string) => void
  onCancelNote: () => void
}

export function NoteCell({ applicant, isEditing, form, onEditNote, onSaveNote, onCancelNote }: NoteCellProps) {
  // Check if note has meaningful content (not empty or just whitespace)
  const hasNote = applicant.note && applicant.note.trim().length > 0

  return (
    <TableCell className="max-w-[200px] min-w-[200px]">
      {isEditing ? (
        <div className="space-y-2">
          <Textarea
            {...form.register("note")}
            placeholder="Add a note about this candidate..."
            className="min-h-[60px] text-sm"
          />
          <div className="flex space-x-2">
            <Button
              size="sm"
              onClick={() => onSaveNote(applicant.case?._id??'')}
              className="bg-green-600 hover:bg-green-700 text-white"
            >
              <Save className="h-3 w-3 mr-1" />
              Save
            </Button>
            <Button size="sm" variant="outline" onClick={onCancelNote} className="text-gray-600">
              <X className="h-3 w-3 mr-1" />
              Cancel
            </Button>
          </div>
        </div>
      ) : (
        <div className="flex items-start space-x-2">
          <div className="flex-1 min-w-0">
            {hasNote && (
              <div
                className="text-sm text-gray-700 max-w-[180px] max-h-[60px] overflow-hidden leading-tight"
                title={applicant.note}
              >
                <p className="break-words overflow-wrap-anywhere line-clamp-3">{applicant.note}</p>
                {applicant.note.length > 100 && <span className="text-xs text-gray-500 italic">...</span>}
              </div>
            )}
          </div>
          <Button
            size="sm"
            variant={hasNote ? "outline" : "default"}
            onClick={() => onEditNote(applicant._id, applicant.note)}
            className={
              hasNote
                ? "p-1.5 h-auto text-gray-600 hover:text-gray-800 border border-gray-300"
                : "bg-gray-800 hover:bg-gray-700 text-white"
            }
            title={hasNote ? "Edit note" : "Add note"}
          >
            {hasNote ? (
              <Edit className="h-4 w-4" />
            ) : (
              <>
                <StickyNote className="h-4 w-4 mr-1" />
                Add Note
              </>
            )}
          </Button>
        </div>
      )}
    </TableCell>
  )
}
